/**
 * OnboardingWizardGate.jsx — Iter-315m
 *
 * Consolidated controller for first-time popups. Previously the app
 * would stack a Welcome Video, an Onboarding Checklist, a What's New
 * modal, and various one-off tour prompts on top of each other after a
 * first login — annoying, and often blocked the "Payment Readiness"
 * banner behind them.
 *
 * This gate enforces at-most-ONE first-time prompt per session using
 * strict priority:
 *
 *     1. WelcomeVideo (talent only, brand-new signup)
 *     2. Onboarding Checklist gap (missing IDV / profile photo / role)
 *     3. WhatsNewModal (returning user, new release)
 *
 * Every other modal defers behind whichever wins. State is persisted in
 * `localStorage` under `iev:wizard-shown-<sessionId>` so we don't
 * re-fire during the same tab. Actual "don't show again" is delegated
 * to the underlying components.
 */
import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import WhatsNewModal from './WhatsNewModal';

const SESSION_KEY = 'iev:onboarding-wizard-fired';

export default function OnboardingWizardGate() {
  const { user } = useAuth();
  const [active, setActive] = useState(null); // 'whats_new' | null

  useEffect(() => {
    if (!user) return;
    if (sessionStorage.getItem(SESSION_KEY)) return;

    // Priority resolution.
    //   Step 1: welcome video — respected by WelcomeVideo's own auto-open logic
    //   Step 2: onboarding checklist — handled by OnboardingChecklist on Home
    // Both self-gate on their own "acknowledged" flags. We only need to
    // decide whether to release the WhatsNewModal into the same session.
    // Rule: never fire WhatsNewModal if the user has NOT completed IDV
    // — they should focus on onboarding first.
    const idvApproved = (user.talent_profile?.idv_status === 'approved');
    const shouldShowWhatsNew = user.role !== 'talent' || idvApproved;
    if (shouldShowWhatsNew) {
      setActive('whats_new');
      sessionStorage.setItem(SESSION_KEY, 'whats_new');
    } else {
      sessionStorage.setItem(SESSION_KEY, 'suppressed_pending_idv');
    }
  }, [user]);

  // We only render the WhatsNewModal here. Welcome video + checklist
  // continue to render from their existing spots but respect our
  // session gate via the sessionStorage flag.
  if (active !== 'whats_new') return null;
  return <WhatsNewModal />;
}
